import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { HttpEventType } from '@angular/common/http';
import { of, switchMap, map, catchError, mergeMap } from 'rxjs';
import { FileUploadService } from '../../../core/services/file-upload.service';
import { FileUploadActions } from './file-upload.actions';
import { IFileUploadError } from '../../models/fileupload.model';

@Injectable()
export class FileUploadEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly fileUploadService: FileUploadService,
    private readonly store: Store
  ) {}

  // Generate Presigned URL Effect
  generatePresignedUrl$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FileUploadActions.generatePresignedUrl),
      switchMap(({ request, file }) =>
        this.fileUploadService.generatePresignedUrl(request).then(
          (response) =>
            FileUploadActions.generatePresignedUrlSuccess({ response, file }),
          (error) =>
            FileUploadActions.generatePresignedUrlFailure({
              error: this.mapError(error),
            })
        )
      )
    )
  );

  // Auto-trigger upload after successful presigned URL generation
  autoTriggerUpload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FileUploadActions.generatePresignedUrlSuccess),
      map(({ response, file }) =>
      {
        console.log(response, file);

        return FileUploadActions.uploadFileToS3({
          presignedUrl: response.url,
          file,
          requestId: response.requestId,
          url: response.key
        });
      }
      )
    )
  );

  // Upload File to S3 Effect
  uploadFileToS3$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FileUploadActions.uploadFileToS3),
      mergeMap(({ presignedUrl, file, requestId,url }) => {
        console.log(presignedUrl, file, requestId);

        return this.fileUploadService.uploadFileToS3(presignedUrl, file).pipe(
          map((event) => {
            if (event.type === HttpEventType.UploadProgress) {
              const progress = event.total
                ? Math.round((100 * event.loaded) / event.total)
                : 0;
              return FileUploadActions.uploadFileToS3Progress({
                progress: {
                  requestId,
                  progress,
                  status: 'uploading',
                },
              });
            } else if (event.type === HttpEventType.Response) {
              return FileUploadActions.uploadFileToS3Success({
                requestId,
                key: '',
                url, // Key is already stored from presigned URL response
              });
            }
            return FileUploadActions.uploadFileToS3Progress({
              progress: {
                requestId,
                progress: 0,
                status: 'uploading',
              },
            });
          }),
          catchError((error) =>
            of(
              FileUploadActions.uploadFileToS3Failure({
                error: this.mapError(error, requestId),
              })
            )
          )
        );
      })
    )
  );

  // Complete Upload Process Effect (Alternative approach)
  uploadFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FileUploadActions.uploadFile),
      switchMap(({ request, file }) =>
        this.fileUploadService.generatePresignedUrl(request).then(
          (presignedResponse) => {
            // Trigger the S3 upload
            this.store.dispatch(
              FileUploadActions.uploadFileToS3({
                presignedUrl: presignedResponse.url,
                file,
                requestId: presignedResponse.requestId,
                url: presignedResponse.key
              })
            );

            return FileUploadActions.generatePresignedUrlSuccess({
              response: presignedResponse,
              file,
            });
          },
          (error) =>
            FileUploadActions.uploadFileFailure({
              error: this.mapError(error),
            })
        )
      )
    )
  );

  // Map upload success to complete upload success
  mapUploadSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FileUploadActions.uploadFileToS3Success),
      map(({ requestId, key,url }) =>
        FileUploadActions.uploadFileSuccess({
          requestId,
          key,
          fileName: '', // This could be retrieved from state if needed
          url
        })
      )
    )
  );

  /**
   * Maps error to standardized error format
   */
  private mapError(error: unknown, requestId?: string): IFileUploadError {
    const err = error as { message?: string; error?: { message?: string }; status?: string; code?: string };
    return {
      requestId,
      message:
        err?.message || err?.error?.message || 'An unknown error occurred',
      code: err?.status || err?.code,
    };
  }
}
